/**
 * Skills Page
 *
 * Displays the skill taxonomy catalog and user progress.
 * Shows domains, categories, and objectives with completion status.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  useGetFullCatalogQuery,
  useGetMyProgressQuery,
  useGetCatalogStatsQuery,
  useGetCategoryProgressQuery,
  type CategoryProgress,
  type SkillDomain,
  type SkillCategory,
  type SkillObjective,
  type DifficultyLevel,
} from '../store/api/skillsApi';
import {
  useGetRecommendedDifficultyQuery,
  useGetAssessmentStatsQuery,
} from '../store/api/assessmentApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';


/**
 * RC19: the skills flow's runs are UNSCORED practice. The authority path has
 * no evidence-based evaluator (backend AuthorityScoringService returns null for
 * every score), so nothing here proves, validates or certifies anything, and
 * the copy says so. A run's result page keeps its honest "not scored".
 */
export const SKILL_PRACTICE_COPY = {
  intro:
    "Practice the skills; scoring isn't available yet. Pick a domain and answer the questions — your answers are recorded, but no score, pass or readiness level is produced.",
} as const;

/** Shown where a score/pass would be while no evaluator exists — never 0. */
const NOT_SCORED = 'Not scored';

// ============================================
// HELPER COMPONENTS
// ============================================

function DifficultyBadge({ difficulty }: { difficulty: DifficultyLevel }) {
  const colors = {
    beginner: 'bg-green-100 text-green-700',
    intermediate: 'bg-yellow-100 text-yellow-700',
    advanced: 'bg-red-100 text-red-700',
  };

  return (
    <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', colors[difficulty])}>
      {difficulty}
    </span>
  );
}

function ProgressRing({
  percentage,
  size = 48,
  strokeWidth = 4,
}: {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#6366f1"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-semibold text-gray-700">{Math.round(percentage)}%</span>
      </div>
    </div>
  );
}

function StatsCard({
  label,
  value,
  icon,
  color = 'primary',
}: {
  label: string;
  value: string | number;
  icon: string;
  color?: 'primary' | 'green' | 'yellow' | 'purple';
}) {
  const colors = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="card">
      <div className="card-body flex items-center gap-4">
        <div className={clsx('w-12 h-12 rounded-lg flex items-center justify-center text-xl', colors[color])}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// DOMAIN CARD COMPONENT
// ============================================

interface DomainCardProps {
  domain: SkillDomain;
  isExpanded: boolean;
  onToggle: () => void;
  progressData?: {
    attempted: number;
    passed: number;
    total: number;
  };
  /** Progress could not be loaded: say so, never "0/N completed". */
  progressUnavailable?: boolean;
  /**
   * RC19: runs are not scored (no evaluator), so "completed" (a pass) is not
   * measurable: show how many objectives were practised instead, never "0/N".
   */
  notScored?: boolean;
  onStartAssessment?: (objectiveId: string) => void;
  startingObjectiveId?: string | null;
}

export function DomainCard({ domain, isExpanded, onToggle, progressData, progressUnavailable = false, notScored = false, onStartAssessment, startingObjectiveId }: DomainCardProps) {
  const totalObjectives =
    domain.categories?.reduce((sum, cat) => sum + (cat.objectives?.length || 0), 0) || 0;
  const progress = progressData || { attempted: 0, passed: 0, total: totalObjectives };
  const progressPercentage = progress.total > 0 ? (progress.passed / progress.total) * 100 : 0;

  return (
    <div className="card overflow-hidden">
      {/* Domain Header */}
      <button
        onClick={onToggle}
        className="w-full card-body flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-2xl">
            {domain.icon || '📚'}
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">{domain.name}</h3>
            <p className="text-sm text-gray-500 line-clamp-1">{domain.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium text-gray-700">
              {progressUnavailable
                ? 'Progress unavailable'
                : notScored
                ? `${progress.attempted}/${progress.total} practised`
                : `${progress.passed}/${progress.total} completed`}
            </p>
            <p className="text-xs text-gray-500">
              {domain.categories?.length || 0} categories
            </p>
          </div>
          {!progressUnavailable && !notScored && <ProgressRing percentage={progressPercentage} />}
          <svg
            className={clsx(
              'w-5 h-5 text-gray-400 transition-transform',
              isExpanded && 'rotate-180'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Categories */}
      {isExpanded && domain.categories && domain.categories.length > 0 && (
        <div className="border-t border-gray-100 bg-gray-50">
          {domain.categories.map((category) => (
            <CategorySection
              key={category.id}
              category={category}
              onStartAssessment={onStartAssessment}
              startingObjectiveId={startingObjectiveId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// CATEGORY SECTION COMPONENT
// ============================================

interface CategorySectionProps {
  category: SkillCategory;
  onStartAssessment?: (objectiveId: string) => void;
  startingObjectiveId?: string | null;
}

/**
 * Objectives the caller has PASSED, from GET /skills/progress/category/:id
 * (UserSkillProgress.passed). Only a measured pass counts: attempted-but-not
 * passed, unknown, or a failed progress read is "not completed".
 */
export function passedObjectiveIds(progress: CategoryProgress | undefined): Set<string> {
  return new Set(
    (progress?.objectives ?? []).filter((o) => o.progress?.passed === true).map((o) => o.objectiveId)
  );
}

function CategorySection({ category, onStartAssessment, startingObjectiveId }: CategorySectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  // Read only once the category is opened (one request per opened category).
  const { data: categoryProgress } = useGetCategoryProgressQuery(category.id, { skip: !isExpanded });
  const passedIds = passedObjectiveIds(categoryProgress);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      {/* Category Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center text-sm font-medium text-gray-600">
            {category.orderIndex + 1}
          </div>
          <div className="text-left">
            <h4 className="font-medium text-gray-800">{category.name}</h4>
            <p className="text-xs text-gray-500">
              {category.objectives?.length || 0} objectives
            </p>
          </div>
        </div>
        <svg
          className={clsx(
            'w-4 h-4 text-gray-400 transition-transform',
            isExpanded && 'rotate-180'
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Objectives */}
      {isExpanded && category.objectives && category.objectives.length > 0 && (
        <div className="px-6 pb-4 space-y-2">
          {category.objectives.map((objective) => (
            <ObjectiveRow
              key={objective.id}
              objective={objective}
              isCompleted={passedIds.has(objective.id)}
              onStartAssessment={onStartAssessment}
              isStartingAssessment={startingObjectiveId === objective.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// OBJECTIVE ROW COMPONENT
// ============================================

interface ObjectiveRowProps {
  objective: SkillObjective;
  /** The caller passed this objective (server progress). */
  isCompleted?: boolean;
  onStartAssessment?: (objectiveId: string) => void;
  isStartingAssessment?: boolean;
}

export function ObjectiveRow({ objective, isCompleted = false, onStartAssessment, isStartingAssessment }: ObjectiveRowProps) {
  // Fetch recommended difficulty for this objective
  const { data: difficultyRec } = useGetRecommendedDifficultyQuery(objective.id);

  const recommendedDifficulty = difficultyRec?.recommendedDifficulty;

  return (
    <div
      className={clsx(
        'p-3 rounded-lg border transition-colors',
        isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div
            className={clsx(
              'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
              isCompleted
                ? 'bg-green-500 border-green-500 text-white'
                : 'border-gray-300'
            )}
          >
            {isCompleted && (
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <p className={clsx('text-sm', isCompleted ? 'text-green-800' : 'text-gray-700')}>
              {objective.objectiveText}
            </p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <DifficultyBadge difficulty={objective.difficulty} />
              {recommendedDifficulty && recommendedDifficulty !== 'beginner' && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                  Recommended: {recommendedDifficulty}
                </span>
              )}
              {objective.requiresSpokenAssessment && (
                <span className="text-xs text-gray-400" title="Requires spoken assessment">
                  🎤
                </span>
              )}
              {objective.requiresKnowledgeAssessment && (
                <span className="text-xs text-gray-400" title="Requires knowledge assessment">
                  📝
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Assessment Button */}
        <button
          onClick={() => onStartAssessment?.(objective.id)}
          disabled={isStartingAssessment}
          className={clsx(
            'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex-shrink-0',
            isCompleted
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-primary-100 text-primary-700 hover:bg-primary-200',
            isStartingAssessment && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isStartingAssessment ? (
            <span className="flex items-center gap-1">
              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Starting...
            </span>
          ) : isCompleted ? (
            'Practice again'
          ) : (
            'Start practice'
          )}
        </button>
      </div>
    </div>
  );
}

// ============================================
// MAIN PAGE COMPONENT
// ============================================

export function SkillsPage() {
  const [expandedDomainId, setExpandedDomainId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch catalog and progress data
  const { data: catalog, isLoading: catalogLoading, error: catalogError, refetch: refetchCatalog } = useGetFullCatalogQuery();
  const {
    data: progressSummary,
    isLoading: progressLoading,
    isError: progressFailed,
    error: progressError,
    refetch: refetchProgress,
  } = useGetMyProgressQuery();
  const { data: stats, isError: statsFailed, error: statsError, refetch: refetchStats } = useGetCatalogStatsQuery();
  const {
    data: assessmentStats,
    isError: assessmentStatsFailed,
    error: assessmentStatsError,
    refetch: refetchAssessmentStats,
  } = useGetAssessmentStatsQuery();

  // RC19: either read says scoring is unavailable → passes and averages are "Not scored".
  const scoringUnavailable =
    progressSummary?.scoringAvailable === false || assessmentStats?.scoringAvailable === false;

  // Navigation for assessment
  const navigate = useNavigate();

  // Handle starting an assessment - navigate to the run page
  const handleStartAssessment = (objectiveId: string) => {
    navigate(`/assessments/${objectiveId}/run`);
  };

  // Filter domains by search
  const filteredDomains = catalog?.filter((domain) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (domain.name.toLowerCase().includes(query)) return true;
    if (domain.description.toLowerCase().includes(query)) return true;
    return domain.categories?.some(
      (cat) =>
        cat.name.toLowerCase().includes(query) ||
        cat.objectives?.some((obj) => obj.objectiveText.toLowerCase().includes(query))
    );
  });

  const handleToggleDomain = (domainId: string) => {
    setExpandedDomainId(expandedDomainId === domainId ? null : domainId);
  };

  // Get progress for a specific domain
  const getDomainProgress = (domainId: string) => {
    const domainProgress = progressSummary?.progressByDomain.find((p) => p.domainId === domainId);
    if (!domainProgress) return undefined;
    return {
      attempted: domainProgress.attemptedCount,
      passed: domainProgress.passedCount,
      total: domainProgress.totalObjectives,
    };
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Skill practice</h1>
          {/* RC19: no evaluator scores these runs yet — no claim of proof,
              validation or readiness (SKILL_PRACTICE_COPY). */}
          <p className="mt-1 text-gray-500" data-testid="skills-practice-intro">
            {SKILL_PRACTICE_COPY.intro}
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search skills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Progress Summary Stats — a failed load is said, never shown as zeros */}
      {(progressFailed || statsFailed) && (
        <div className="card">
          <div className="card-body">
            <LoadErrorNotice
              error={progressFailed ? progressError : statsError}
              onRetry={() => {
                if (progressFailed) void refetchProgress();
                if (statsFailed) void refetchStats();
              }}
              failedMessage="Your skills progress is unavailable right now."
              testIdPrefix="skills-progress"
            />
          </div>
        </div>
      )}
      {!progressLoading && !progressFailed && !statsFailed && progressSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard
            label="Objectives Completed"
            value={scoringUnavailable ? NOT_SCORED : progressSummary.totalObjectivesPassed}
            icon="✅"
            color="green"
          />
          <StatsCard
            label="Attempted"
            value={progressSummary.totalObjectivesAttempted}
            icon="📊"
            color="primary"
          />
          <StatsCard
            label="Avg Authority Score"
            value={
              typeof progressSummary.averageAuthorityScore === 'number'
                ? `${Math.round(progressSummary.averageAuthorityScore)}%`
                : scoringUnavailable
                ? NOT_SCORED
                : '—'
            }
            icon="⭐"
            color="yellow"
          />
          <StatsCard
            label="Domains"
            value={stats ? stats.activeDomains : '—'}
            icon="📚"
            color="purple"
          />
        </div>
      )}

      {/* Loading State */}
      {catalogLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading skill catalog...</span>
        </div>
      )}

      {/* Error State */}
      {catalogError && (
        <div className="card">
          <div className="card-body py-8">
            <LoadErrorNotice
              error={catalogError}
              onRetry={() => void refetchCatalog()}
              failedMessage="The skill catalog could not be loaded."
              testIdPrefix="skills-catalog"
            />
          </div>
        </div>
      )}

      {/* Empty State */}
      {!catalogLoading && !catalogError && (!filteredDomains || filteredDomains.length === 0) && (
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="text-4xl mb-4">📭</div>
            <h3 className="font-semibold text-gray-900">
              {searchQuery ? 'No matching skills found' : 'No skill domains available'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Skill domains will appear here once they are configured'}
            </p>
          </div>
        </div>
      )}

      {/* Domain Cards */}
      {!catalogLoading && filteredDomains && filteredDomains.length > 0 && (
        <div className="space-y-4">
          {filteredDomains.map((domain) => (
            <DomainCard
              key={domain.id}
              domain={domain}
              isExpanded={expandedDomainId === domain.id}
              onToggle={() => handleToggleDomain(domain.id)}
              progressData={getDomainProgress(domain.id)}
              progressUnavailable={progressFailed}
              notScored={scoringUnavailable}
              onStartAssessment={handleStartAssessment}
              startingObjectiveId={null}
            />
          ))}
        </div>
      )}

      {/* Assessment Stats Section */}
      {assessmentStatsFailed && (
        <div className="card">
          <div className="card-body">
            <h3 className="font-semibold text-gray-900 mb-4">Your Assessment History</h3>
            <LoadErrorNotice
              error={assessmentStatsError}
              onRetry={() => void refetchAssessmentStats()}
              failedMessage="Your assessment history is unavailable right now."
              testIdPrefix="skills-assessment-stats"
            />
          </div>
        </div>
      )}
      {!assessmentStatsFailed && assessmentStats && assessmentStats.totalSessions > 0 && (
        <div className="card">
          <div className="card-body">
            <h3 className="font-semibold text-gray-900 mb-4">Your skill practice history</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary-600">{assessmentStats.totalSessions}</p>
                <p className="text-xs text-gray-500">Total Sessions</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{assessmentStats.completedSessions}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">
                  {typeof assessmentStats.averageScore === 'number'
                    ? `${Math.round(assessmentStats.averageScore)}%`
                    : assessmentStats.scoringAvailable === false
                    ? NOT_SCORED
                    : '—'}
                </p>
                <p className="text-xs text-gray-500">Avg Score</p>
              </div>
              <div className="text-center">
                <div className="flex justify-center gap-1">
                  <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                    B: {assessmentStats.byDifficulty.beginner.count}
                  </span>
                  <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                    I: {assessmentStats.byDifficulty.intermediate.count}
                  </span>
                  <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                    A: {assessmentStats.byDifficulty.advanced.count}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">By Difficulty</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Catalog Stats Footer */}
      {stats && (
        <div className="text-center text-sm text-gray-500 py-4 border-t border-gray-100">
          <span>
            {stats.totalObjectives} objectives across {stats.totalCategories} categories in{' '}
            {stats.activeDomains} domains
          </span>
          <span className="mx-2">•</span>
          <span>
            {stats.objectivesByDifficulty.beginner} beginner, {stats.objectivesByDifficulty.intermediate}{' '}
            intermediate, {stats.objectivesByDifficulty.advanced} advanced
          </span>
        </div>
      )}
    </div>
  );
}
